import { GoogleLogin } from "@react-oauth/google";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { API_LEGACY } from "@/App";

/**
 * Google Sign-In button — custom-styled wrapper around the official
 * `<GoogleLogin>` component so the visual matches our Apple button 1:1.
 *
 * Technique: render `<GoogleLogin>` with the SAME dimensions as our
 * custom button, position the GIS iframe absolutely over our styled
 * <button>, and set the iframe to `opacity: 0`. The user sees OUR
 * button (theme-aware, pure black in dark / pure white in light, no
 * Google-branded white icon container) while clicks still go to the
 * GIS iframe which triggers the exact same redirect flow we already
 * have working in production.
 *
 * Why this approach over a full custom OAuth flow:
 *  - No new env vars (would otherwise need GOOGLE_CLIENT_SECRET)
 *  - No new backend endpoints to maintain
 *  - No risk of regressing the redirect + g_csrf_token logic that
 *    took multiple iterations to harden
 *  - GIS continues to handle Safari/COOP edge cases natively
 */
export const GoogleSignInButton = () => {
  const isDark = useIsDarkMode();

  const buttonClass = [
    "flex items-center justify-center gap-3",
    "w-[320px] h-[44px] rounded-md",
    "font-medium text-sm",
    "border transition-colors",
    isDark
      ? "bg-black text-white border-black"
      : "bg-white text-gray-900 border-gray-300",
  ].join(" ");

  return (
    <div className="flex justify-center" data-testid="google-signin-wrapper">
      <div className="relative w-[320px] h-[44px]">
        {/* The visible button — purely cosmetic */}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className={`${buttonClass} absolute inset-0 z-0 pointer-events-none`}
          data-testid="google-signin-button-visual"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 488 512"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path
              fill="#4285F4"
              d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
            />
          </svg>
          <span>Sign in with Google</span>
        </button>
        {/* The invisible GIS button on top — handles the click */}
        <div className="absolute inset-0 z-10 opacity-0">
          <GoogleLogin
            ux_mode="redirect"
            login_uri={`${API_LEGACY}/auth/google/web/callback`}
            size="large"
            width="320"
            theme="outline"
            text="signin_with"
            shape="rectangular"
            useOneTap={false}
          />
        </div>
      </div>
    </div>
  );
};

export default GoogleSignInButton;
